import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, WritableSignal, computed, input, model, signal } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { ChartData, ChartOptions } from 'chart.js';
import { DateTime } from 'luxon';
import { BaseChartDirective, provideCharts } from 'ng2-charts';
import { MessageService } from 'primeng/api';
import { Observable, combineLatest, of, switchMap, zip } from 'rxjs';
import { ProfileLocation } from 'src/app/api/linkable-locations.service';

import { ChartOptionsService } from 'src/app/services/chart-options.service';
import { ErrorService } from 'src/app/services/error.service';
import { TripActualHistory, TripActualHistoryGQL, TripActualHistoryLocationGQL, TripActualHistoryLocationMetrics, TripActualHistoryLocationQuery, TripActualHistoryMetrics, TripActualHistoryQuery, TripActualHistorySort } from 'src/generated/graphql';

interface LocationDatasetResults { year: number, data: Exclude<TripActualHistoryLocationQuery['tripActualHistoryLocation'], null | undefined> }

@Component({
  selector: 'app-trip-actual-history-graph',
  imports: [BaseChartDirective],
  templateUrl: './trip-actual-history-graph.component.html',
  styleUrl: './trip-actual-history-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TripActualHistoryGraphComponent implements OnInit, OnChanges {

  constructor(

    private chartOptionService: ChartOptionsService,
    private overallHistory: TripActualHistoryGQL,
    private locationHistory: TripActualHistoryLocationGQL,
    private messageService: MessageService,
    private errorService: ErrorService
  ) {

  }



  @Output() loading = new EventEmitter<boolean>()


  @Input() locations: ProfileLocation[] = []

  @Input({ required: true }) years: number[] = []

  colorList = [
    "#e06666",
    "#ffd966",
    "#93c47d",
    "#76a5af",
    "#8e7cc3",
    "#c27ba0",
    "#cc4125",
    "#f6b26b"
  ]

  chartOptions: ChartOptions<"line"> = {
    ...this.chartOptionService.getBasicLineChart({ disableAnimations: true, hidePoints: true }),

    interaction: {
      mode: "index",
      intersect: false
    },
    hover: {
      mode: "x"
    },

    plugins: {

      legend: {
        position: "bottom"
      }


    }
  }

  overallScales: ChartOptions<"line">["scales"] = {

    y: {
      ticks: {
        stepSize: 2000,
        callback(tickValue, index, ticks) {
          return tickValue.toLocaleString()
        },
      }
    },
    x: {
      display: false
    }

  }

  locationScales: ChartOptions<"line">["scales"] = {

    y: {
      ticks: {
        stepSize: 100,
        callback(tickValue, index, ticks) {
          return tickValue.toLocaleString()
        },
      }
    },
    x: {
      display: false
    }

  }



  overallResults: WritableSignal<{ year: number, data: Exclude<TripActualHistoryQuery['tripActualHistory'], null | undefined> }[]> = signal([])

  locationResults: WritableSignal<LocationDatasetResults[]> = signal([])

  chartData = computed(() => {

    const locationData = this.locationResults()
    const overallData = this.overallResults()

    let returnData: ChartData<"line", number[], string> = {
      datasets: [],
      //generate two years of labels using a non-leap year.  Feb 29 is removed when datasets are generated
      labels: [...this.generateLabelsForYear(2019), ...this.generateLabelsForYear(2019)]
    }

    let i = 0;

    if (this.locations.length > 0) {

      this.chartOptions.scales = this.locationScales


      for (const location of locationData) {
        if (location.data.length > 0) {
          returnData.datasets.push({
            data: location.data.map(l => l.value),
            label: `${location.year}`,
            pointBackgroundColor: this.colorList[i % 8],
            pointHoverBackgroundColor: this.colorList[i % 8],
            pointHoverBorderColor: this.colorList[i % 8],
            borderColor: this.colorList[i % 8],
            backgroundColor: "rgba(0,0,0,0)"
          })
        }
        i++ //always increment so the year color stays consistent
      }

    }
    else {

      this.chartOptions.scales = this.overallScales


      for (const year of overallData) {

        returnData.datasets.push({
          data: year.data.map(d => d.value),
          label: year.year.toString(),
          pointBackgroundColor: this.colorList[i % 8],
          pointHoverBackgroundColor: this.colorList[i % 8],
          pointHoverBorderColor: this.colorList[i % 8],
          borderColor: this.colorList[i % 8],
          backgroundColor: "rgba(0,0,0,0)"
        })

        i++;

      }

    }



    return returnData
  })


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locations'] || changes['years']) {
      if (changes['locations']?.currentValue && changes['locations']?.currentValue.length > 0) {
        this.updateLocationData()
      }
      else {
        this.updateOverallData()
      }
    }
  }



  ngOnInit(): void {

    this.updateOverallData()
    this.updateLocationData()


  }

  updateOverallData() {

    this.loading.emit(true)
    const observables: Observable<{ year: number, next: ApolloQueryResult<TripActualHistoryQuery>, current: ApolloQueryResult<TripActualHistoryQuery> }>[] = []

    //FIXME: Update the dates here once we pull trip years from the server on startup
    zip(
      this.overallHistory.fetch({ params: { startDate: `${this.years[0] - 1}-01-01`, endDate: "2049-12-31", metrics: [TripActualHistoryMetrics.PpmNext] } }),
      this.overallHistory.fetch({ params: { startDate: `${this.years[0]}-01-01`, endDate: "2050-12-31", metrics: [TripActualHistoryMetrics.PpmCurrent] } })
    ).subscribe({
      next: ([ppmNext, ppmCurrent]) => {
        if (ppmNext.data.tripActualHistory && ppmCurrent.data.tripActualHistory) {
          let years: number[] = []
          for (const record of ppmNext.data.tripActualHistory) {
            let year = Number(record.date.substring(0, 4)) + 1
            if (!years.includes(year)) {
              years.push(year)
            }
          }

          this.overallResults.set(years.map(year => {
            return {
              year: year,
              data: [...ppmNext.data.tripActualHistory?.filter(h => h.date.startsWith((year - 1).toString()) && !h.date.includes("-02-29")) ?? [], ...ppmCurrent.data.tripActualHistory?.filter(h => h.date.startsWith(year.toString()) && !h.date.includes("-02-29")) ?? []]
            }
          }))

        }

        this.loading.emit(false)
      }
    })

  }

  updateLocationData() {

    if (this.locations.length > 0) {
      this.loading.emit(true)
      const observables: Observable<any>[] = []



      const locIds = this.locations.map(l => l.id)
      //FIXME: Update the dates here once we pull trip years from the server on startup
      zip(
        this.locationHistory.fetch({ params: { startDate: `${this.years[0] - 1}-01-01`, endDate: '2049-12-31', metrics: [TripActualHistoryLocationMetrics.LocationNext], dimensions: locIds, sort: [TripActualHistorySort.Dimension, TripActualHistorySort.Date] } }),
        this.locationHistory.fetch({ params: { startDate: `${this.years[0]}-01-01`, endDate: '2050-12-31', metrics: [TripActualHistoryLocationMetrics.LocationCurrent], dimensions: locIds, sort: [TripActualHistorySort.Dimension, TripActualHistorySort.Date] } })
      ).pipe(
        switchMap(([ppmNext, ppmCurrent]) => {
          if (ppmNext.data.tripActualHistoryLocation && ppmCurrent.data.tripActualHistoryLocation) {

            let years: number[] = Array.from(new Set(ppmNext.data.tripActualHistoryLocation.map(d => d.date.substring(0, 4)))).map(d => Number(d)).sort((a, b) => a - b)

            let nextDatasets = this.combineLocationDatasets(years.map(year => {
              const next = ppmNext.data.tripActualHistoryLocation ?? []

              return {
                year: year,
                data: next.filter(h => h.date.startsWith((year).toString()) && !h.date.includes("-02-29")),

              }
            }))

            let currentDatasets = this.combineLocationDatasets(years.map(year => {

              const current = ppmCurrent.data.tripActualHistoryLocation ?? []
              return {
                year: year + 1,
                data: current.filter(h => h.date.startsWith((year + 1).toString()) && !h.date.includes("-02-29")),

              }
            }))

            let result = nextDatasets.map(n => {
              return {
                year: n.year + 1,
                next: n.data,
                current: currentDatasets.find(c => c.year === n.year + 1)?.data
              }
            })

            return of(result)

          }
          else {
            return of([])
          }
        })
      ).subscribe(
        resultArray => {
          this.locationResults.set(resultArray.map(r => {
            const currentData = r.current ?? []
            const nextData = r.next ?? []

            return {
              year: r.year,
              data: [...(this.fillEmptyLocationMetrics(nextData.filter((d: any) => !d.date.includes("-02-29")), true)), ...this.fillEmptyLocationMetrics(currentData.filter((d: any) => !d.date.includes("02-29")), false)]
            }
          }))
          this.loading.emit(false)
        }
      )

    }

  }

  /**
   * Fills in gaps in the dataset so the data points match up with the labels.  Fills the beginning with zeroes, gaps in the middle with the previous
   * value, and leaves gaps at the end.
   * @param data 
   * @param fillToEnd 
   * @returns 
   */
  fillEmptyLocationMetrics(data: TripActualHistoryLocationQuery['tripActualHistoryLocation'], fillToEnd: boolean) {

    //NOTE: This assumes the data is for a single location/metric pair

    if (data && data.length > 0) {

      let returnData: TripActualHistoryLocationQuery['tripActualHistoryLocation'] = []

      let currentDate = DateTime.fromISO(`${data[0].date.substring(0, 4)}-01-01`)
      let previousValue = 0;

      for (let i = 0; i < data.length; i++) {
        let checkDate = DateTime.fromISO(data[i].date)
        while (currentDate < checkDate) {
          returnData.push({
            date: currentDate.toFormat("yyyy-MM-dd"),
            //Use the previous value while we're in the middle of the series to keep the line graph smooth.  We likely don't have data b/c the cron job
            //didn't run, not because the value was zero
            value: previousValue,
            __typename: "TripActualHistoryLocation",
            location: data[0].location
          })
          currentDate = currentDate.plus({ day: 1 })
        }
        returnData.push(data[i])
        currentDate = currentDate.plus({ day: 1 })
        previousValue = data[i].value
      }

      if (fillToEnd) {
        let endDate = DateTime.fromFormat(data[0].date, 'yyyy-mm-dd')
        const now = DateTime.now()

        if (now < endDate) endDate = now //don't fill dates that haven't happened yet


        while (DateTime.fromFormat(returnData[returnData.length - 1].date, 'yyyy-MM-dd') < endDate) {
          const data = returnData[returnData.length - 1]
          returnData.push({
            date: DateTime.fromISO(data.date).plus({ day: 1 }).toFormat("yyyy-MM-dd"),
            value: 0, //FIll to end with zero, since if there are no metrics, the value would have been zero
            location: data.location,
            __typename: "TripActualHistoryLocation"
          })
        }
      }
      return returnData
    }

    return []

  }

  /**
   * Sums the yearly datasets for all locations for the same year into a single dataset per year
   * @param data 
   * @returns 
   */
  combineLocationDatasets(data: LocationDatasetResults[]) {

    const returnData: LocationDatasetResults[] = []

    for (const year of data) {

      const daymap = new Map<string, typeof year.data[number]>()

      for (const day of year.data) {
        let daymapDay = daymap.get(day.date)
        daymapDay ? daymapDay.value += day.value : daymap.set(day.date, JSON.parse(JSON.stringify(day)))
      }

      returnData.push({ ...year, data: Array.from(daymap.values()).sort((a, b) => a.date.localeCompare(b.date)) })
    }

    return returnData

  }


  generateLabelsForYear(year: number) {

    let currentDate = DateTime.fromFormat(`${year}-01-01`, 'yyyy-mm-dd')

    const labels: string[] = []

    while (currentDate.year === year) {
      labels.push(currentDate.toFormat('LLLL dd'))
      currentDate = currentDate.plus({ day: 1 })
    }


    return labels

  }

}
